import { Injectable } from '@angular/core';
import { FlutterAction } from '../../domain/flutter-communication.model';

/**
 * Flutter Bridge Service
 * Handles communication between Angular webview and Flutter parent application
 *
 * @description
 * This service provides methods to send messages to Flutter using multiple communication channels:
 * 1. PostMessage API (primary)
 * 2. Legacy Flutter APIs (fallback)
 * 3. Console logging for debugging
 *
 * Communication Flow:
 * Angular -> Flutter: Action strings only (per Flutter team requirements)
 * Flutter -> Angular: Event listeners for incoming messages
 */
@Injectable({ providedIn: 'root' })
export class FlutterBridgeService {

    /**
     * Send message to Flutter parent application
     * @param action - Action identifier (string only, per Flutter requirements)
     * @param data - Additional data for logging/debugging (not sent to Flutter)
     * @param targetOrigin - Target origin for postMessage (default: '*')
     *
     * @description
     * Per Flutter team feedback, only action strings are sent to Flutter.
     * Data parameter is used for console logging and debugging purposes only.
     */
    sendToFlutter(action: string, data: any = null, targetOrigin: string = '*'): void {
        console.log('📱 Flutter Bridge: Sending action to Flutter:', action);
            if (data) {
                console.log('📊 Flutter Bridge: Associated data (debug only):', data);
            }

        try {
            // Primary communication method: PostMessage API
            if (this.isInIframe()) {
                console.log('📱 Using PostMessage API to communicate with Flutter');
                window.parent.postMessage(action, targetOrigin);
            } else {
                console.log('📱 Not in iframe, using fallback Flutter APIs');
                this.sendViaLegacyFlutterAPIs(action, data);
            }
        } catch (error) {
            console.error('❌ Flutter Bridge: Failed to send message to Flutter:', error);
            console.error('📱 Action that failed:', action);

            // Attempt fallback communication
            try {
                this.sendViaLegacyFlutterAPIs(action, data);
            } catch (fallbackError) {
                console.error('❌ Flutter Bridge: Fallback communication also failed:', fallbackError);
            }
        }
    }

    /**
     * Send OTP verification success to Flutter
     * @param response - OTP verification response data
     */
    sendOtpVerified(response: any): void {
        console.log('✅ Flutter Bridge: Sending OTP verified signal');
        console.log('📊 Response data:', response);

        this.sendToFlutter(FlutterAction.OTP_VERIFIED, response);

        console.log('📱 Expected Flutter action: Navigate to PIN setup with response data');
    }

    /**
     * Send OTP blocked notification to Flutter
     * @param error - Error details for OTP blocking
     */
    sendOtpBlocked(error: any): void {
        console.log('🔒 Flutter Bridge: Sending OTP blocked signal');
        console.log('📊 Error details:', error);

        this.sendToFlutter(FlutterAction.OTP_BLOCKED, error);

        console.log('📱 Expected Flutter action: Show blocked state or navigate to alternative flow');
    }

    /**
     * Send OTP blocked notification to Flutter
     * @param error - Error details for OTP blocking
     */
    sendBindingSuccess(response: any): void {
        console.log('🔒 Flutter Bridge: Sending Binding Successful signal');
        console.log('📊 Error details:', response);

        this.sendToFlutter(FlutterAction.BINDING_SUCCESS, response);

        console.log('📱 Expected Flutter action: Show Successfull Binding Message (bottomsheet)');
    }

    /**
     * Send general error to Flutter
     * @param error - Error details
     * @param action - Specific action type (default: ERROR)
     */
    sendError(error: any, action: string = FlutterAction.ERROR): void {
        console.log('❌ Flutter Bridge: Sending error signal');
        console.log('📊 Error details:', error);
        console.log('📱 Action:', action);

        this.sendToFlutter(action, error);

        console.log('📱 Expected Flutter action: Handle error state or show error message');
    }

    /**
     * Check if running inside an iframe
     * @returns true if in iframe
     */
    private isInIframe(): boolean {
        try {
            return window.self !== window.top;
        } catch (e) {
            // If we can't access window.top due to cross-origin restrictions,
            // we're likely in an iframe
            return true;
        }
    }

    /**
     * Send message via legacy Flutter APIs (fallback method)
     * @param action - Action string
     * @param data - Data for debugging
     *
     * @description
     * This method attempts to use various Flutter communication channels
     * that might be available in different Flutter webview implementations.
     */
    private sendViaLegacyFlutterAPIs(action: string, data: any): void {
        const attempts = [
            // Flutter WebView postMessage
            () => {
                if ((window as any).flutter_inappwebview) {
                    (window as any).flutter_inappwebview.callHandler('messageHandler', action);
                    console.log('📱 Sent via flutter_inappwebview.callHandler');
                    return true;
                }
                return false;
            },

            // Flutter WebView channel
            () => {
                if ((window as any).FlutterChannel) {
                    (window as any).FlutterChannel.postMessage(action);
                    console.log('📱 Sent via FlutterChannel.postMessage');
                    return true;
                }
                return false;
            },

            // Flutter WebView bridge
            () => {
                if ((window as any).FlutterBridge) {
                    (window as any).FlutterBridge.send(action);
                    console.log('📱 Sent via FlutterBridge.send');
                    return true;
                }
                return false;
            },

            // Generic Flutter communication
            () => {
                if ((window as any).Flutter) {
                    (window as any).Flutter.postMessage(action);
                    console.log('📱 Sent via Flutter.postMessage');
                    return true;
                }
                return false;
            },

            // Android WebView interface
            () => {
                if ((window as any).AndroidInterface) {
                    (window as any).AndroidInterface.sendMessage(action);
                    console.log('📱 Sent via AndroidInterface.sendMessage');
                    return true;
                }
                return false;
            },

            // iOS WebKit message handler
            () => {
                if ((window as any).webkit?.messageHandlers?.flutterHandler) {
                    (window as any).webkit.messageHandlers.flutterHandler.postMessage(action);
                    console.log('📱 Sent via webkit.messageHandlers.flutterHandler');
                    return true;
                }
                return false;
            },
        ];

        let success = false;
        for (const attempt of attempts) {
            try {
                if (attempt()) {
                    success = true;
                    break;
                }
            } catch (error) {
                console.warn('📱 Flutter Bridge: Communication attempt failed:', error);
            }
        }

        if (!success) {
            console.warn('⚠️ Flutter Bridge: No Flutter communication channel found');
            console.warn('📱 Action that could not be sent:', action);
            if (data) {
                console.warn('📊 Associated data:', data);
            }
        }
    }

    /**
     * Test Flutter communication (development only)
     */
    testCommunication(): void {
        console.log('🧪 Flutter Bridge: Testing communication channels...');

        const testAction = 'test_communication';
        const testData = {
            timestamp: new Date().toISOString(),
            source: 'angular_webview',
            test: true,
        };

        this.sendToFlutter(testAction, testData);

        // Log available Flutter interfaces
        console.log('📱 Available Flutter interfaces:');
        console.log('- flutter_inappwebview:', !!(window as any).flutter_inappwebview);
        console.log('- FlutterChannel:', !!(window as any).FlutterChannel);
        console.log('- FlutterBridge:', !!(window as any).FlutterBridge);
        console.log('- Flutter:', !!(window as any).Flutter);
        console.log('- AndroidInterface:', !!(window as any).AndroidInterface);
        console.log('- webkit.messageHandlers:', !!(window as any).webkit?.messageHandlers);
        console.log('- In iframe:', this.isInIframe());
    }

    /**
     * Listen for messages from Flutter
     * @param callback - Callback function to handle incoming messages
     * @returns Cleanup function to remove the listener
     */
    listenToFlutter(callback: (message: any) => void): () => void {
        const handler = (event: MessageEvent) => {
            console.log('📱 Flutter Bridge: Received message from Flutter:', event.data);
            console.log('📊 Message origin:', event.origin);
            callback(event.data);
        };

        window.addEventListener('message', handler);

        // Return cleanup function
        return () => {
            window.removeEventListener('message', handler);
            console.log('📱 Flutter Bridge: Message listener removed');
        };
    }
}